/* ============================================================
   sticky-media.js — keeps the product image visible while the
   info column scrolls.

   PROBLEM
   -------
   `.product-media` is a float column whose intrinsic height = the
   gallery's height (often shorter than the info column). For
   `position: sticky` on the inner wrapper to work over the entire
   product section, the column itself must be at least as tall as
   the sibling `.product-info`.

   SOLUTION
   --------
   Mirror `.product-info`'s computed height onto `.product-media--sticky`
   as `min-height`, refreshed on:
     - DOMContentLoaded (initial)
     - resize (rAF-throttled)
     - ResizeObserver on `.product-info` (variant changes can reflow)
     - `on:variant:change` (theme broadcast)
     - `shopify:section:load` (theme editor)

   We also freeze a stable `--sticky-media-top` to avoid the jitter
   caused by the theme's header rewriting `--header-end-padded` on
   scroll.

   PERFORMANCE
   -----------
   - All DOM writes are rAF-throttled to coalesce bursts.
   - ResizeObservers are tracked and disconnected before re-creation
     so section reloads can't leak observers.
   - Heights are written only when they change (no redundant style writes).
   - Below 769px we bail entirely (mobile keeps natural flow).
   ============================================================ */

(() => {
  if (window.__stickyMediaSyncLoaded) return;
  window.__stickyMediaSyncLoaded = true;

  const MIN_VIEWPORT_PX = 769;        // match `@media (min-width: 769px)` in product-page.css
  const STICKY_TOP_PADDING = 16;      // gap between (shrunk) header and pinned image

  /** Per-element ResizeObservers, so we can disconnect old ones on section reload. */
  const observers = new WeakMap();
  /** Cached last-applied height per element to skip no-op writes. */
  const lastApplied = new WeakMap();

  /** rAF throttle — coalesces multiple rapid invocations into one frame. */
  function rafThrottle(fn) {
    let pending = false;
    return () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; fn(); });
    };
  }

  /**
   * Read the (post-shrunk) header height once and write it to our own
   * CSS variable. Crucially, this does NOT bind to scroll — the value
   * stays stable so the sticky `top:` doesn't jitter as the theme's
   * header expands/contracts.
   */
  function setStableStickyTop() {
    const header = document.querySelector('.site-header') || document.querySelector('header');
    let topPx = 80;
    if (header) {
      const r = header.getBoundingClientRect();
      const h = Math.min(r.height, header.clientHeight) || r.height;
      topPx = Math.round(h + STICKY_TOP_PADDING);
    }
    document.documentElement.style.setProperty('--sticky-media-top', `${topPx}px`);
  }

  function syncOne(mediaEl) {
    if (window.innerWidth < MIN_VIEWPORT_PX) {
      if (mediaEl.style.minHeight) mediaEl.style.removeProperty('min-height');
      lastApplied.delete(mediaEl);
      return;
    }
    const product = mediaEl.closest('.product, .product-main');
    if (!product) return;
    const info = product.querySelector('.product-info');
    if (!info) return;
    const next = Math.round(info.getBoundingClientRect().height);
    if (next <= 0) return;
    // Skip no-op writes — avoids redundant style invalidation.
    if (lastApplied.get(mediaEl) === next) return;
    mediaEl.style.minHeight = `${next}px`;
    lastApplied.set(mediaEl, next);
  }

  function syncAll() {
    document.querySelectorAll('.product-media--sticky').forEach(syncOne);
  }

  const syncAllThrottled = rafThrottle(() => {
    setStableStickyTop();
    syncAll();
  });

  function attachObservers() {
    if (!('ResizeObserver' in window)) return;
    document.querySelectorAll('.product-media--sticky').forEach((mediaEl) => {
      // If we've already observed this element, disconnect and re-attach
      // — necessary because section reloads can replace the .product-info
      // node, leaving the old observation pointing at a detached element.
      const prev = observers.get(mediaEl);
      if (prev) prev.disconnect();
      const product = mediaEl.closest('.product, .product-main');
      const info = product && product.querySelector('.product-info');
      if (!info) return;
      const throttled = rafThrottle(() => syncOne(mediaEl));
      const ro = new ResizeObserver(throttled);
      ro.observe(info);
      observers.set(mediaEl, ro);
    });
  }

  function init() {
    setStableStickyTop();
    syncAll();
    attachObservers();
  }

  // Initial bind
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('resize', syncAllThrottled);
  document.addEventListener('on:variant:change', syncAllThrottled, true);
  document.addEventListener('shopify:section:load', () => {
    // Theme editor replaced DOM under us — re-attach observers and re-sync.
    setTimeout(() => { attachObservers(); syncAllThrottled(); }, 0);
  });
})();
